import {
  SupportSessionClient,
  type SupportSessionInfo,
} from '@droidmap/admin-service-contract'
import { createServiceEndpoint } from '@droidmap/frontend-env'
import {
  StandardOrgIds,
  StandardUserIds,
} from '@droidmap/shared-service-contract'

/**
 * A staff member's support sessions session (droidmap#2651).
 *
 * The session lives in this tab's `sessionStorage` and nowhere else: never in
 * Amplify's `localStorage`, so a customer signed in on the same browser keeps
 * their own session. Closing the tab does not end it: the browser restores a
 * reopened or duplicated tab's `sessionStorage`, and the session stays live
 * until it is ended (the banner's End) or expires.
 */
export type SupportSession = {
  token: string
  session: SupportSessionInfo
}

/** Where the admin console sends the new tab: `/support-session#code=…&to=…`. */
export const SUPPORT_SESSION_PATH = '/support-session'

const STORAGE_KEY = 'droidmap.support-session'

const isSession = (value: unknown): value is SupportSession => {
  const v = value as Partial<SupportSession> | undefined
  return (
    typeof v?.token === 'string' &&
    typeof v.session?.targetUserId === 'string' &&
    typeof v.session.expiresAt === 'string'
  )
}

/** The session this tab holds, whether or not it has expired. */
export const readStoredSupportSession = (): SupportSession | undefined => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : undefined
    return isSession(parsed) ? parsed : undefined
  } catch {
    return undefined
  }
}

/** The tab's session, if it holds one and it has not expired. */
export const readSupportSession = (
  now: number = Date.now(),
): SupportSession | undefined => {
  const stored = readStoredSupportSession()
  return stored && new Date(stored.session.expiresAt).getTime() > now
    ? stored
    : undefined
}

/**
 * The one organisation this tab's support session works in, or `undefined`
 * when the tab holds no session or its session names none. Assisting, the
 * org it acts in; observing, the user's org the staff member named. The
 * services refuse a session's requests in any other org, and count its
 * grants only in that one.
 */
export const supportSessionOrgId = (): string | undefined =>
  readStoredSupportSession()?.session.orgId

export const writeSupportSession = (session: SupportSession): void => {
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session))
}

/**
 * Keep only a mark that this tab's session is over: its details, so a reload
 * opens on "Session ended" again rather than on a customer sign-in, with the
 * token dropped and the expiry moved to now. The tab's `sessionStorage` is
 * its own, so a customer sign-in in another tab is untouched; a new session
 * started into this tab overwrites the mark.
 */
export const markSupportSessionEnded = (now: number = Date.now()): void => {
  const stored = readStoredSupportSession()
  if (!stored) {
    return
  }
  const expiresAt = Math.min(now, new Date(stored.session.expiresAt).getTime())
  try {
    writeSupportSession({
      token: '',
      session: {
        ...stored.session,
        expiresAt: new Date(expiresAt).toISOString(),
      },
    })
  } catch {
    clearSupportSession()
  }
}

/**
 * Keep a support session's links in its own tab (droidmap#2651). A session
 * lives in its tab's `sessionStorage`, which a tab opened from a link does
 * not get, so a ctrl-, cmd- or shift-click or a middle click would open the
 * customer's page signed in as whoever the browser holds (the staff member's
 * own account, if any), with no banner. Such a click on a link into the app
 * navigates here instead. Answers the cleanup.
 *
 * @example
 * useEffect(() => keepLinksInThisTab(document, window.location), [])
 */
export const keepLinksInThisTab = (
  doc: Document,
  location: Pick<Location, 'origin' | 'assign'>,
): (() => void) => {
  const onClick = (event: MouseEvent) => {
    const newTab =
      event.button === 1 ||
      (event.button === 0 && (event.ctrlKey || event.metaKey || event.shiftKey))
    if (!newTab || !(event.target instanceof Element)) {
      return
    }
    const link = event.target.closest('a[href]')
    // A link that opens its own tab is meant to: the banner's Extend opens
    // the staff console, whose sign-in must never share this tab's storage.
    if (
      !(link instanceof HTMLAnchorElement) ||
      link.origin !== location.origin ||
      (link.target !== '' && link.target !== '_self') ||
      link.pathname === '/admin' ||
      link.pathname.startsWith('/admin/')
    ) {
      return
    }
    event.preventDefault()
    location.assign(link.href)
  }
  // Capturing, ahead of the router's own link handling; `auxclick` is the
  // middle button's.
  doc.addEventListener('click', onClick, true)
  doc.addEventListener('auxclick', onClick, true)
  return () => {
    doc.removeEventListener('click', onClick, true)
    doc.removeEventListener('auxclick', onClick, true)
  }
}

export const clearSupportSession = (): void => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage that cannot be written cannot hold a session either.
  }
}

/**
 * Whether this page load is a support session. Read once, when the module
 * loads, because it picks which auth provider the app runs under and that
 * choice must not change while the app is mounted. Starting or ending a
 * session reloads the page.
 *
 * An expired session still counts: the tab opens on "Session ended", which
 * clears it. Treating it as none would fall back to whatever customer
 * sign-in the browser holds, so a staff member who is also a customer would
 * land on their own account with no banner and no sign the session is over.
 */
export const SUPPORT_SESSION_AT_LOAD: SupportSession | undefined =
  typeof window === 'undefined' ? undefined : readStoredSupportSession()

/** Whether the app is running as a staff member observing as a customer. */
export const isInSupportSession = (): boolean =>
  SUPPORT_SESSION_AT_LOAD !== undefined

/**
 * `to` as a path on this origin, or `/`. It comes from a link, so it must not
 * be able to send a fresh support session anywhere else. Resolved the way the
 * browser will resolve it, because string checks miss `/\evil.com` and
 * `/\t/evil.com`, which browsers read as another host.
 */
export const sameOriginPath = (
  to: string,
  origin: string = window.location.origin,
): string => {
  try {
    const url = new URL(to, origin)
    return url.origin === origin && to.startsWith('/')
      ? `${url.pathname}${url.search}${url.hash}`
      : '/'
  } catch {
    return '/'
  }
}

/** The `code` and `to` the admin console put in the URL fragment. */
export const parseSupportSessionFragment = (
  hash: string,
): { code?: string; to: string } => {
  const params = new URLSearchParams(hash.replace(/^#/, ''))
  return {
    code: params.get('code') ?? undefined,
    to: sameOriginPath(params.get('to') ?? '/'),
  }
}

const sessionClient = (env: string, token = '') =>
  new SupportSessionClient({
    getToken: async () => token,
    endpoint: createServiceEndpoint('admin', env),
    droidMapMetadata: {
      userId: StandardUserIds.SYSTEM,
      orgId: StandardOrgIds.SYSTEM,
    },
  })

/** Redeem the console's one-time code and keep the session in this tab. */
export const exchangeSupportSessionCode = async (
  code: string,
  env: string,
): Promise<SupportSession> => {
  const result = await sessionClient(env).exchangeCode({ code })
  const session: SupportSession = {
    token: result.token,
    // The client revives ISO instants into Dates; keep the wire strings.
    session: {
      ...result.session,
      expiresAt: new Date(result.session.expiresAt).toISOString(),
    },
  }
  writeSupportSession(session)
  return session
}

/**
 * Ask the admin service whether the session is still live. Resolves `false`
 * when it says the session is over, or that it cannot verify the token at
 * all: it issued the token, so that means its signing key has changed since
 * (rotated or replaced), and no service will accept the token again.
 * Anything else (the service briefly down, a network blip) resolves `true`,
 * and the next check asks again.
 */
export const isSupportSessionLive = async (
  session: SupportSession,
  env: string,
): Promise<boolean> => {
  try {
    await sessionClient(env, session.token).getSessionStatus()
    return true
  } catch (error) {
    const message = (error as { serverMessage?: unknown }).serverMessage
    return !(
      message === 'support_session_revoked' ||
      message === 'support_session_expired' ||
      message === 'support_session_invalid'
    )
  }
}

/**
 * End the session: record the end with the admin service, best effort, then
 * drop the token, keeping the mark that the tab's session ended. The token
 * is dropped even when the call fails, so ending always ends it for this tab.
 */
export const endSupportSession = async (
  session: SupportSession,
  env: string,
): Promise<void> => {
  try {
    await sessionClient(env, session.token).endSession()
  } finally {
    markSupportSessionEnded()
  }
}
